import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { 
  Calendar, 
  Plus, 
  Loader2, 
  Pencil, 
  Trash2,
  Flag,
  Globe,
  Download,
  FileDown
} from 'lucide-react';
import { format, parseISO, getYear } from 'date-fns';
import api from '../services/api';
import { getApiError } from '@/lib/utils';

export default function HolidaysPage() {
  const { canManageSites } = useAuth();
  const { region } = useSettings();
  const { toast } = useToast();
  
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regionFilter, setRegionFilter] = useState('');
  const [yearFilter, setYearFilter] = useState(getYear(new Date()).toString());
  const [includeInactive, setIncludeInactive] = useState(false);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  
  // Import form data
  const [importYear, setImportYear] = useState(getYear(new Date()).toString());
  const [importRegion, setImportRegion] = useState('BOTH');
  
  // Form data
  const [formData, setFormData] = useState({
    date: '',
    name: '',
    region: 'UK',
    is_active: true
  });

  const loadHolidays = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (regionFilter) params.append('region', regionFilter);
      if (yearFilter) params.append('year', yearFilter);
      if (includeInactive) params.append('include_inactive', 'true');
      
      const response = await api.get(`/holidays?${params.toString()}`);
      setHolidays(response.data);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load holidays',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast, regionFilter, yearFilter, includeInactive]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openCreateDialog = () => {
    setSelectedHoliday(null);
    setFormData({
      date: format(new Date(), 'yyyy-MM-dd'),
      name: '',
      region: region || 'UK',
      is_active: true
    });
    setDialogOpen(true);
  };

  const openEditDialog = (holiday) => {
    setSelectedHoliday(holiday);
    setFormData({
      date: holiday.date,
      name: holiday.name,
      region: holiday.region,
      is_active: holiday.is_active
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (holiday) => {
    setSelectedHoliday(holiday);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (selectedHoliday) {
        await api.put(`/holidays/${selectedHoliday.id}`, formData);
        toast({ title: 'Success', description: 'Holiday updated' });
      } else {
        await api.post('/holidays', formData);
        toast({ title: 'Success', description: 'Holiday created' });
      }
      setDialogOpen(false);
      setSaving(false);
      loadHolidays();
    } catch (err) {
      setSaving(false);
      const errorDetail = err.response?.data?.detail;
      toast({
        title: 'Error',
        description: typeof errorDetail === 'string' ? errorDetail : 'Failed to save holiday',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedHoliday) return;
    
    try {
      await api.delete(`/holidays/${selectedHoliday.id}`);
      toast({ title: 'Success', description: 'Holiday deleted' });
      setDeleteDialogOpen(false);
      loadHolidays();
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to delete holiday'),
        variant: 'destructive'
      });
    }
  };

  // Import functions
  const openImportDialog = () => {
    setImportYear(yearFilter || getYear(new Date()).toString());
    setImportRegion('BOTH');
    setImportPreview(null);
    setImportDialogOpen(true);
  };

  const handlePreviewImport = async () => {
    try {
      setImporting(true);
      const response = await api.post('/holidays/import/preview', {
        year: parseInt(importYear),
        region: importRegion
      });
      setImportPreview(response.data);
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to preview import'),
        variant: 'destructive'
      });
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async () => {
    try {
      setImporting(true);
      const response = await api.post('/holidays/import', {
        year: parseInt(importYear),
        region: importRegion
      });
      toast({
        title: 'Success',
        description: response.data.message
      });
      setImportDialogOpen(false);
      setImportPreview(null);
      loadHolidays();
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to import holidays'),
        variant: 'destructive'
      });
    } finally {
      setImporting(false);
    }
  };

  // Generate year options (current year -1 to +2)
  const currentYear = getYear(new Date());
  const yearOptions = Array.from({ length: 4 }, (_, i) => (currentYear - 1 + i).toString());

  if (!canManageSites) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Permission denied</p>
      </div>
    );
  }

  return (
    <div data-testid="holidays-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Calendar className="h-6 w-6" />
            Bank Holidays
          </h1>
          <p className="text-muted-foreground">Manage bank holidays for UK and UAE regions</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={openImportDialog} variant="outline" data-testid="import-holiday-btn">
            <Download className="h-4 w-4 mr-2" />
            Import Holidays
          </Button>
          <Button onClick={openCreateDialog} data-testid="add-holiday-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Holiday
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="space-y-2">
              <Label>Year</Label>
              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger className="w-[120px]" data-testid="year-filter">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(year => (
                    <SelectItem key={year} value={year}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Region</Label>
              <Select value={regionFilter || 'all'} onValueChange={(v) => setRegionFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[150px]" data-testid="region-filter">
                  <SelectValue placeholder="All Regions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  <SelectItem value="UK">
                    <div className="flex items-center gap-2">
                      <span>🇬🇧</span> UK
                    </div>
                  </SelectItem>
                  <SelectItem value="UAE">
                    <div className="flex items-center gap-2">
                      <span>🇦🇪</span> UAE
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="inactive"
                checked={includeInactive}
                onCheckedChange={setIncludeInactive}
                data-testid="include-inactive-toggle"
              />
              <Label htmlFor="inactive" className="text-sm">Show Inactive</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Holidays Table */}
      <Card>
        <CardHeader>
          <CardTitle>Holidays</CardTitle>
          <CardDescription>
            {holidays.length} holiday{holidays.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : holidays.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No holidays found</p>
              <Button onClick={openCreateDialog} variant="outline" className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Add first holiday
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {holidays.map((holiday) => (
                  <TableRow 
                    key={holiday.id} 
                    className={!holiday.is_active ? 'opacity-50' : ''}
                    data-testid={`holiday-row-${holiday.id}`}
                  >
                    <TableCell>
                      <div className="font-medium">
                        {format(parseISO(holiday.date), 'EEE, MMM d, yyyy')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Flag className="h-4 w-4 text-muted-foreground" />
                        {holiday.name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={
                        holiday.region === 'UAE' 
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                          : 'bg-blue-50 border-blue-200 text-blue-700'
                      }>
                        <Globe className="h-3 w-3 mr-1" />
                        {holiday.region === 'UAE' ? '🇦🇪 UAE' : '🇬🇧 UK'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {holiday.is_active ? (
                        <Badge variant="secondary" className="bg-green-100 text-green-800">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-gray-100 text-gray-600">
                          Inactive
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(holiday)}
                          title="Edit"
                          data-testid={`edit-holiday-${holiday.id}-btn`}
                          className="h-8 w-8"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteDialog(holiday)}
                          title="Delete"
                          data-testid={`delete-holiday-${holiday.id}-btn`}
                          className="h-8 w-8"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedHoliday ? 'Edit Holiday' : 'Add Holiday'}
            </DialogTitle>
            <DialogDescription>
              {selectedHoliday 
                ? 'Update the holiday details'
                : 'Add a new bank holiday for the calendar'
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Holiday Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g., Christmas Day"
                  required
                  data-testid="holiday-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleChange('date', e.target.value)}
                    required
                    data-testid="holiday-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="region">Region *</Label>
                  <Select 
                    value={formData.region} 
                    onValueChange={(v) => handleChange('region', v)}
                  >
                    <SelectTrigger data-testid="holiday-region-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UK">
                        <div className="flex items-center gap-2">
                          <span>🇬🇧</span> UK
                        </div>
                      </SelectItem>
                      <SelectItem value="UAE">
                        <div className="flex items-center gap-2">
                          <span>🇦🇪</span> UAE
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => handleChange('is_active', checked)}
                  data-testid="holiday-active-switch"
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={saving || !formData.name || !formData.date}
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {selectedHoliday ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Holiday?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedHoliday && (
                <>
                  Are you sure you want to delete <strong>{selectedHoliday.name}</strong> ({format(parseISO(selectedHoliday.date), 'd MMM yyyy')})?
                  <br /><br />
                  This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Holidays Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileDown className="h-5 w-5" />
              Import Holidays
            </DialogTitle>
            <DialogDescription>
              Import standard bank holidays for UK and/or UAE for a specific year.
              Existing holidays will not be duplicated.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="import-year">Year *</Label>
                <Select value={importYear} onValueChange={setImportYear}>
                  <SelectTrigger id="import-year" data-testid="import-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 6 }, (_, i) => (currentYear - 1 + i).toString()).map(year => (
                      <SelectItem key={year} value={year}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="import-region">Region *</Label>
                <Select value={importRegion} onValueChange={setImportRegion}>
                  <SelectTrigger id="import-region" data-testid="import-region-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UK">
                      <div className="flex items-center gap-2">
                        <span>🇬🇧</span> UK Only
                      </div>
                    </SelectItem>
                    <SelectItem value="UAE">
                      <div className="flex items-center gap-2">
                        <span>🇦🇪</span> UAE Only
                      </div>
                    </SelectItem>
                    <SelectItem value="BOTH">
                      <div className="flex items-center gap-2">
                        <span>🌍</span> Both UK & UAE
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Preview Results */}
            {importPreview && (
              <Card className="bg-muted/50">
                <CardContent className="pt-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Total holidays available:</span>
                      <span className="font-medium">{importPreview.total_holidays}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Already exist (will skip):</span>
                      <span className="font-medium text-muted-foreground">{importPreview.existing_count}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span>To be imported:</span>
                      <span className="font-medium text-green-600">{importPreview.to_import}</span>
                    </div>
                  </div>
                  
                  {importPreview.to_import > 0 && (
                    <div className="mt-4 max-h-[200px] overflow-y-auto">
                      <p className="text-xs text-muted-foreground mb-2">Holidays to import:</p>
                      <div className="space-y-1">
                        {importPreview.holidays.map((h, i) => (
                          <div key={i} className="flex justify-between text-xs py-1 border-b border-muted">
                            <span>{h.name}</span>
                            <span className="text-muted-foreground">
                              {format(parseISO(h.date), 'd MMM')} • {h.region}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setImportDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handlePreviewImport}
              disabled={importing}
              data-testid="preview-import-btn"
            >
              {importing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Preview
            </Button>
            <Button 
              type="button"
              onClick={handleImport}
              disabled={importing || (importPreview && importPreview.to_import === 0)}
              data-testid="confirm-import-btn"
            >
              {importing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Import {importPreview ? `(${importPreview.to_import})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
